import React, { FunctionComponent, Suspense, useMemo, useState } from 'react';
import { useEmailHygieneWizardQuery } from 'app/src/components/EmailHygieneWizard/EmailHygieneWizardQuery.generated';
import { useLazyComponent } from '@trello/use-lazy-component';
import { Spinner } from 'app/src/components/Spinner';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';

import { AtlassianAccountMigrationStage } from './types';

interface Props {
  stage: AtlassianAccountMigrationStage | null;
  attributes: object;
  onComplete: () => void;
  onDismiss: () => void;
}

export const AtlassianAccountMigrationStageOverlay: FunctionComponent<Props> = ({
  stage,
  attributes,
  onComplete,
  onDismiss,
}) => {
  // Store the `stage` prop on mount. While the overlay is open, this should
  // never change; let consumers handle the logic for dismissing the overlay.
  const [initialStage] = useState<AtlassianAccountMigrationStage | null>(stage);

  const AtlassianAccountMigrationWizard = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "aa-migration-wizard" */ 'app/src/components/AtlassianAccountMigration/AtlassianAccountMigrationWizard'
      ),
    {
      preload: initialStage === AtlassianAccountMigrationStage.migration,
    },
  );
  const EmailHygieneWizard = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "email-hygiene-wizard" */ 'app/src/components/EmailHygieneWizard/EmailHygieneWizard'
      ),
    {
      preload: initialStage === AtlassianAccountMigrationStage.emailHygiene,
    },
  );

  /**
   * The wizards take in different props, but today the PEH query is a superset
   * of the latter (except email, which we're fetching extraneously in some
   * the banners to use useAtlassianAccountMigrationStage, and delegate the
   * cases), so we can get away with it.
   */
  const { loading, data } = useEmailHygieneWizardQuery({
    variables: { memberId: 'me' },
  });
  const me = data?.member;

  const analyticsContext = useMemo(
    () => ({
      ...attributes,
      organizationId: null,
      totalEmailAddresses: me?.logins?.length,
      totalClaimableEmailAddresses: me?.logins?.filter((l) => l.claimable)
        .length,
    }),
    [me, attributes],
  );

  const renderWizard = () => {
    if (!me) {
      return null;
    }
    switch (initialStage) {
      case AtlassianAccountMigrationStage.migration:
        return (
          <AtlassianAccountMigrationWizard
            analyticsContext={analyticsContext}
            email={me.email ?? ''}
            hasTwoFactor={!!me.prefs?.twoFactor?.enabled}
            onDismiss={onDismiss}
          />
        );
      case AtlassianAccountMigrationStage.emailHygiene:
        return (
          <EmailHygieneWizard
            analyticsContext={analyticsContext}
            avatarUrl={me.avatarUrl ?? undefined}
            hasTwoFactor={!!me.prefs?.twoFactor?.enabled}
            initials={me.initials ?? undefined}
            logins={me.logins}
            name={me.fullName || me.username}
            onComplete={onComplete}
            onDismiss={onDismiss}
            isUndismissable={false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Overlay
        background={OverlayBackground.LIGHT}
        closeOnEscape={false}
        onClose={() => {}}
        alignment={OverlayAlignment.TOP}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Suspense fallback={null}>{renderWizard()}</Suspense>
        )}
      </Overlay>
    </>
  );
};

// Utility function to determine whether the overlay would be functional.
export const isStageActionable = (
  stage: AtlassianAccountMigrationStage | null,
) =>
  stage === AtlassianAccountMigrationStage.migration ||
  stage === AtlassianAccountMigrationStage.emailHygiene;
